import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "@/api/client";
import {
  useBrandOverlay,
  useBrandPreview,
  useDeleteBrandingAsset,
  useResetBrand,
  useUpdateBrand,
  type BrandingAsset,
  type BrandOverlay,
  type ThemeAdminState,
} from "@/api/queries";
import { BrandPreview } from "@/components/theming/BrandPreview";
import {
  COLOR_KEYS,
  ColorEditor,
  type ColorForm,
  type Pair,
} from "@/components/theming/ColorEditor";
import { FontEditor, type FontForm } from "@/components/theming/FontEditor";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Field } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Textarea } from "@/components/ui/Textarea";
import { AlertIcon, MailIcon, TrashIcon, UploadIcon } from "@/components/icons";
import { useBrand } from "@/theme/context";
import type { FontRole, FontSpec } from "@/theme/fontPresets";
import { cn } from "@/lib/cn";
import { useDebounced } from "@/lib/useDebounced";

/**
 * White-label theming (PRD FR-11.1, FR-11.2).
 *
 * A rebrand here reaches the app shell, the unlock page, the printed PDF, the
 * HTML export and the emailed report, because all of them read the same
 * theme document — and, since the save now swaps the live stylesheet, it
 * reaches this page the moment it is applied.
 *
 * **It writes an overlay, not `tokens.json`.** The deployment mounts that file
 * read-only, so an editor that wrote to it would work in development and fail
 * in production. Every field shows whether it is the file's value or this
 * deployment's, and "Reset" drops the overlay rather than restoring a backup.
 *
 * **One colour, previewed before it is applied.** The ramp, the dark-mode
 * accent and the accent-linked platform tokens are derived on the server
 * (`app/theme/derive.py`); the light and dark previews are painted from
 * `POST /admin/theme/preview`, so what is shown is what will ship, warnings
 * included.
 */

const COPY_FIELDS = [
  "name",
  "tagline",
  "assistantName",
  "vendor",
  "welcomeTitle",
  "welcomeBody",
  "emailFooter",
  "logoLight",
  "logoDark",
  "favicon",
] as const;
type CopyField = (typeof COPY_FIELDS)[number];
/** Fields that cannot sensibly be blank: emptying one means "the file's". Every
 *  other copy field may be blanked on purpose — no footer, no tagline, no logo. */
const NEVER_BLANK = new Set<CopyField>(["name", "assistantName"]);
const ROLES: FontRole[] = ["display", "body", "mono"];

interface FormState extends Record<CopyField, string> {
  /** What this deployment chose — empty sides are "automatic" or the file's. */
  color: ColorForm;
  /** `null` is the file's typeface for that role. */
  font: FontForm;
  /** Copy fields to hand back to the file on the next apply. */
  inherit: CopyField[];
}

type StoredOverlay = {
  color?: Record<string, Partial<Pair> | undefined>;
  font?: Record<string, FontSpec | undefined>;
};

function toForm(state: ThemeAdminState): FormState {
  const brand = state.document.brand as unknown as Record<
    string,
    string | null | undefined
  >;
  const overlay = state.overlay as StoredOverlay;
  const copy = Object.fromEntries(
    COPY_FIELDS.map((key) => [key, brand[key] ?? ""]),
  ) as Record<CopyField, string>;
  const color = Object.fromEntries(
    COLOR_KEYS.map((key) => [
      key,
      {
        light: overlay.color?.[key]?.light ?? "",
        dark: overlay.color?.[key]?.dark ?? "",
      },
    ]),
  ) as ColorForm;
  const font = Object.fromEntries(
    ROLES.map((role) => {
      const spec = overlay.font?.[role];
      return [
        role,
        spec
          ? {
              family: spec.family,
              stack: spec.stack,
              source: spec.source ?? null,
            }
          : null,
      ];
    }),
  ) as FontForm;
  return { ...copy, color, font, inherit: [] };
}

const samePair = (a: Pair, b: Pair) => a.light === b.light && a.dark === b.dark;
const sameFont = (a: FontSpec | null, b: FontSpec | null) =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Only what changed, so untouched fields keep inheriting the file. `color`
 * and `font` are sent per key — the server merges them one level down, so a
 * second save no longer forgets the first.
 */
function toPayload(form: FormState, base: FormState): BrandOverlay {
  const body: Record<string, unknown> = {};
  for (const key of COPY_FIELDS) {
    // `null` inherits the file; an empty string is a blank somebody chose,
    // which is the only way to say "no footer" when the file has one.
    if (form.inherit.includes(key)) body[key] = null;
    else if (form[key] !== base[key])
      body[key] = NEVER_BLANK.has(key) ? form[key] || null : form[key];
  }
  const color: Record<string, Partial<Pair> | null> = {};
  for (const key of COLOR_KEYS) {
    const next = form.color[key];
    if (samePair(next, base.color[key])) continue;
    color[key] =
      next.light || next.dark
        ? {
            ...(next.light ? { light: next.light } : {}),
            ...(next.dark ? { dark: next.dark } : {}),
          }
        : null;
  }
  if (Object.keys(color).length) body.color = color;
  const font: Record<string, FontSpec | null> = {};
  for (const role of ROLES) {
    if (!sameFont(form.font[role], base.font[role]))
      font[role] = form.font[role];
  }
  if (Object.keys(font).length) body.font = font;
  return body as BrandOverlay;
}

/**
 * A fresh server state, with the reader's unsaved edits kept on top.
 *
 * Uploading a logo refetches the state; resetting the whole form from it threw
 * away every other edit on the page — including the logo path just chosen.
 */
function rebase(
  form: FormState,
  oldBase: FormState,
  newBase: FormState,
): FormState {
  const next: FormState = {
    ...newBase,
    color: { ...newBase.color },
    font: { ...newBase.font },
    inherit: [...form.inherit],
  };
  for (const key of COPY_FIELDS)
    if (form[key] !== oldBase[key]) next[key] = form[key];
  for (const key of COLOR_KEYS) {
    if (!samePair(form.color[key], oldBase.color[key]))
      next.color[key] = form.color[key];
  }
  for (const role of ROLES) {
    if (!sameFont(form.font[role], oldBase.font[role]))
      next.font[role] = form.font[role];
  }
  return next;
}

/**
 * Says whether a field is the file's value or this deployment's, and offers
 * the way back. "Reset to file" resets everything; this resets one field.
 */
function FieldOrigin({
  state,
  form,
  field,
  hint,
  onInherit,
}: {
  state: ThemeAdminState;
  form: FormState;
  field: CopyField;
  hint?: string;
  onInherit: (inherit: boolean) => void;
}) {
  const pending = form.inherit.includes(field);
  if (!pending && !state.overridden.includes(field)) return <>{hint}</>;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {pending ? (
        <>
          <Chip>the file's, once applied</Chip>
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={() => onInherit(false)}
          >
            Undo
          </button>
        </>
      ) : (
        <>
          <Chip tone="accent">changed here</Chip>
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={() => onInherit(true)}
          >
            Use the file's
          </button>
        </>
      )}
      {hint}
    </span>
  );
}

function formatBytes(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} MB`;
  if (value >= 1_000) return `${Math.round(value / 1_000)} KB`;
  return `${value} B`;
}

function AssetSlot({
  slot,
  label,
  hint,
  value,
  assets,
  disabled,
  onChange,
  onUploaded,
}: {
  slot: string;
  label: string;
  hint: string;
  value: string;
  assets: BrandingAsset[];
  disabled?: boolean;
  onChange: (next: string) => void;
  onUploaded: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const removeAsset = useDeleteBrandingAsset();

  // The file this slot has uploaded, if any — which is what can be deleted,
  // independently of whether this slot is currently pointing at it.
  const stored = assets.find((asset) => asset.uploaded && asset.slot === slot);
  const storedName = stored?.path.replace("/branding/", "") ?? "";
  const onDisk = assets.some((asset) => asset.path === value);

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await fetch(`/api/admin/theme/assets/${slot}`, {
        method: "POST",
        body,
        credentials: "same-origin",
      });
      const payload: unknown = await response.json().catch(() => undefined);
      if (!response.ok) {
        const message =
          typeof payload === "object" && payload !== null && "error" in payload
            ? (payload as { error: { message: string } }).error.message
            : `Upload failed (${response.status}).`;
        throw new Error(message);
      }
      onChange((payload as { path: string }).path);
      onUploaded();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-border rounded-element flex flex-col gap-2 border p-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="type-label text-ink w-32">{label}</span>
        {value && onDisk ? (
          <img
            src={value}
            alt=""
            className="bg-card border-border max-h-10 max-w-32 rounded-inner border object-contain p-1"
          />
        ) : value ? (
          // tokens.json names `/branding/logo-light.svg` whether or not the
          // image ships one; a broken <img> here read as "the logo is broken".
          <span className="type-support text-ink-disabled">
            <span className="font-mono">{value}</span> is not on disk — the
            drawn mark is used
          </span>
        ) : (
          <span className="type-support text-ink-disabled">none</span>
        )}
        <div className="ms-auto flex items-center gap-2">
          <Button
            type="button"
            icon={<UploadIcon className="size-4" />}
            loading={busy}
            disabled={disabled}
            onClick={() => input.current?.click()}
          >
            Upload
          </Button>
          {value ? (
            <Button
              type="button"
              variant="ghost"
              disabled={disabled}
              onClick={() => onChange("")}
              title="Stop using this file here. The file itself is kept."
            >
              Clear
            </Button>
          ) : null}
          {stored ? (
            <Button
              type="button"
              variant="ghost"
              icon={<TrashIcon className="size-4" />}
              disabled={disabled}
              onClick={() => setConfirming(true)}
            >
              Delete file
            </Button>
          ) : null}
        </div>
        <input
          ref={input}
          type="file"
          accept="image/svg+xml,image/png,image/jpeg,image/webp,image/x-icon"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void upload(file);
            event.target.value = "";
          }}
        />
      </div>
      <p className="type-support text-ink-secondary">{hint}</p>
      {stored ? (
        // "Clear" and "Delete file" are genuinely different actions, so the
        // page says which file is on disk and how much of the volume it is
        // using rather than leaving the difference to be guessed.
        <p className="type-caption text-ink-disabled normal-case">
          On disk: {storedName} · {formatBytes(stored.bytes)}
        </p>
      ) : null}
      {assets.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="type-caption text-ink-disabled">Available:</span>
          {assets.map((asset) => (
            <button
              key={asset.path}
              type="button"
              disabled={disabled}
              onClick={() => onChange(asset.path)}
              className={cn(
                "rounded-full border px-2 py-0.5 type-support motion-fast",
                asset.path === value
                  ? "border-accent text-accent"
                  : "border-border text-ink-secondary hover:bg-hover",
              )}
            >
              {asset.path.replace("/branding/", "")}
            </button>
          ))}
        </div>
      ) : null}
      {error ? <p className="type-support text-error">{error}</p> : null}
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`Delete ${label.toLowerCase()} file?`}
        description={
          <>
            <span className="font-mono">{storedName}</span> is removed from this
            deployment's data volume.
          </>
        }
        consequence="Anything in the branding still pointing at it goes back to the file's value, or to the drawn mark."
        loading={removeAsset.isPending}
        onConfirm={() => {
          setError(null);
          removeAsset.mutate(slot, {
            onSuccess: () => setConfirming(false),
            onError: (caught) =>
              setError(
                caught instanceof ApiError
                  ? caught.message
                  : "Could not delete that file.",
              ),
          });
        }}
      />
    </div>
  );
}

export function ThemingPage() {
  const { data: state, isLoading, refetch } = useBrandOverlay();
  const save = useUpdateBrand();
  const reset = useResetBrand();
  const brand = useBrand();
  const [form, setForm] = useState<FormState | null>(null);
  const [base, setBase] = useState<FormState | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const baseRef = useRef<FormState | null>(null);
  // Set while an apply or reset is in flight: the state it returns replaces
  // the form outright. Rebasing would keep the very edits it just applied —
  // an emptied field the server resolved to the file's text stayed "unsaved"
  // forever.
  const replaceNext = useRef(false);

  useEffect(() => {
    if (!state) return;
    const next = toForm(state);
    const replace = replaceNext.current;
    replaceNext.current = false;
    setForm((current) =>
      current && baseRef.current && !replace
        ? rebase(current, baseRef.current, next)
        : next,
    );
    setBase(next);
    baseRef.current = next;
  }, [state]);

  const payload = useMemo(
    () => (form && base ? toPayload(form, base) : ({} as BrandOverlay)),
    [form, base],
  );
  const dirty = Object.keys(payload).length > 0;
  // Debounced so a colour picker dragged across the wheel asks once it
  // settles, not once per pixel.
  const deferred = useDebounced(payload, 250);
  const preview = useBrandPreview(deferred, Boolean(state) && dirty);

  if (isLoading || !state || !form || !base) {
    return (
      <div className="mx-auto flex w-full max-w-4xl items-center gap-2 px-4 py-10 sm:px-8">
        <Spinner />
        <span className="type-support text-ink-secondary">
          Loading branding…
        </span>
      </div>
    );
  }

  const editable = state.editable;
  const shown = dirty && preview.data ? preview.data.document : state.document;
  const warnings =
    (dirty && preview.data ? preview.data.warnings : state.warnings) ?? [];
  const previewError =
    dirty && preview.error instanceof ApiError ? preview.error.message : null;
  const error = save.error instanceof ApiError ? save.error.message : null;
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));
  const inherit = (field: CopyField) => (on: boolean) =>
    setForm((current) =>
      current
        ? {
            ...current,
            [field]: base[field],
            inherit: on
              ? [...current.inherit.filter((key) => key !== field), field]
              : current.inherit.filter((key) => key !== field),
          }
        : current,
    );
  const origin = (field: CopyField, text?: string) => (
    <FieldOrigin
      state={state}
      form={form}
      field={field}
      hint={text}
      onInherit={inherit(field)}
    />
  );
  const locked = (field: CopyField) =>
    !editable || form.inherit.includes(field);
  const footerPreview = form.emailFooter
    .replaceAll("{name}", form.name || brand.productName)
    .replaceAll("{vendor}", form.vendor);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-10">
      <header className="mb-8">
        <p className="type-caption text-ink-disabled">Admin</p>
        <h1 className="type-h1 text-ink mt-2">Branding</h1>
        <p className="type-body text-ink-secondary mt-3 max-w-2xl">
          Name, copy, colour, typefaces and logos. Applying a change re-themes
          this page at once, and reaches the unlock page, every export — PDF,
          HTML, Markdown — and scheduled emails on their next request.
        </p>
        <p className="type-support text-ink-secondary mt-3">
          Defaults come from{" "}
          <span className="text-ink font-mono">{state.tokens_path}</span>, which
          is mounted read-only. What you change here is stored in this
          deployment's database and layered on top.
          {state.updated_at ? (
            <> Last changed {new Date(state.updated_at).toLocaleString()}.</>
          ) : null}
        </p>
      </header>

      {!editable ? (
        <div className="border-warning/30 bg-warning-subtle/40 rounded-container mb-6 flex items-start gap-2 border p-4">
          <AlertIcon className="text-warning mt-0.5 size-4 shrink-0" />
          <p className="type-support text-ink-secondary">
            This deployment's branding is owned by its mounted{" "}
            <code>tokens.json</code> (
            <span className="font-mono">MING_THEME_ADMIN_ENABLED=false</span>).
            Edit the file, or turn the setting on.
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-5 pb-4">
        <Card>
          <CardHeader
            title="Identity"
            description="What this deployment calls itself"
          />
          <CardBody className="flex flex-col gap-4">
            <Field
              label="Product name"
              hint={origin(
                "name",
                "Shown in the sidebar, the browser tab, the API docs, the PDF letterhead, download filenames and the email From name.",
              )}
              value={form.name}
              disabled={locked("name")}
              onChange={(event) => set("name", event.target.value)}
            />
            <Field
              label="Tagline"
              hint={origin(
                "tagline",
                "Beside the name on the unlock page and the printed footer. Rename the product without setting one and the file's tagline is left out, since it was written for the file's name.",
              )}
              value={form.tagline}
              disabled={locked("tagline")}
              onChange={(event) => set("tagline", event.target.value)}
            />
            <Field
              label="Assistant name"
              hint={origin(
                "assistantName",
                "How the agent introduces itself. Never a literal in a component — the prompt reads this.",
              )}
              value={form.assistantName}
              disabled={locked("assistantName")}
              onChange={(event) => set("assistantName", event.target.value)}
            />
            <Field
              label="Vendor"
              hint={origin(
                "vendor",
                "Who runs this deployment. Substituted into copy as {vendor}.",
              )}
              value={form.vendor}
              disabled={locked("vendor")}
              onChange={(event) => set("vendor", event.target.value)}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Copy"
            description="The words on the front door and at the foot of every export"
          />
          <CardBody className="flex flex-col gap-4">
            <Field
              label="Welcome title"
              hint={origin(
                "welcomeTitle",
                "The home page's greeting. Leave empty for the platform's own.",
              )}
              value={form.welcomeTitle}
              disabled={locked("welcomeTitle")}
              onChange={(event) => set("welcomeTitle", event.target.value)}
            />
            <Textarea
              label="Welcome copy"
              hint={origin(
                "welcomeBody",
                "Under the greeting on the home page, and on the unlock page.",
              )}
              rows={3}
              value={form.welcomeBody}
              disabled={locked("welcomeBody")}
              onChange={(event) => set("welcomeBody", event.target.value)}
            />
            <Field
              label="Email and export footer"
              hint={origin(
                "emailFooter",
                "At the foot of every delivered email, PDF, HTML and Markdown export. {name} and {vendor} are substituted. Empty means no footer.",
              )}
              value={form.emailFooter}
              disabled={locked("emailFooter")}
              onChange={(event) => set("emailFooter", event.target.value)}
            />
            <p className="type-support text-ink-secondary inline-flex items-center gap-2">
              <MailIcon className="size-4" />
              Preview: {footerPreview || "— no footer —"}
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Colour"
            description="One brand colour, and everything it implies — previewed in both modes before you apply it"
          />
          <CardBody className="flex flex-col gap-5">
            <ColorEditor
              form={form.color}
              document={shown}
              disabled={!editable}
              onChange={(next) => set("color", next)}
            />
            <div className="grid gap-3 md:grid-cols-2" aria-live="polite">
              <BrandPreview document={shown} scheme="light" />
              <BrandPreview document={shown} scheme="dark" />
            </div>
            {preview.isFetching && dirty ? (
              <span className="type-support text-ink-secondary inline-flex items-center gap-2">
                <Spinner /> Updating the preview…
              </span>
            ) : null}
            {previewError ? (
              <p className="type-support text-error">{previewError}</p>
            ) : null}
            {warnings.length ? (
              <ul className="bg-warning-subtle rounded-element flex flex-col gap-1.5 px-3 py-2">
                {warnings.map((warning) => (
                  <li
                    key={`${warning.code}-${warning.scheme}-${warning.message}`}
                    className="type-support text-ink flex items-start gap-2"
                  >
                    <AlertIcon className="text-warning mt-0.5 size-4 shrink-0" />
                    {warning.message}
                  </li>
                ))}
              </ul>
            ) : null}
            <p className="type-support text-ink-secondary">
              Backgrounds, text and the success, warning and error colours stay
              the platform's, so "error" never reads as "on brand". Chart series
              two to ten keep their order and their colour-blind-safe colours;
              only series one follows the brand.
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Typefaces" description="Headings, text and code" />
          <CardBody>
            <FontEditor
              form={form.font}
              document={shown}
              disabled={!editable}
              onChange={(next) => set("font", next)}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Assets"
            description="Logos and the browser-tab icon"
          />
          <CardBody className="flex flex-col gap-3">
            <AssetSlot
              slot="logoLight"
              label="Logo (light)"
              hint="Used on light backgrounds, in the sidebar and on the PDF letterhead. SVG is best."
              value={form.logoLight}
              assets={state.assets}
              disabled={!editable}
              onChange={(next) => set("logoLight", next)}
              onUploaded={() => void refetch()}
            />
            <AssetSlot
              slot="logoDark"
              label="Logo (dark)"
              hint="Used in dark mode. Falls back to the light logo when there is none — and the light one falls back to this."
              value={form.logoDark}
              assets={state.assets}
              disabled={!editable}
              onChange={(next) => set("logoDark", next)}
              onUploaded={() => void refetch()}
            />
            <AssetSlot
              slot="favicon"
              label="Favicon"
              hint="With none, the tab icon is drawn from the brand colour and the product's initial — in the app and in every exported HTML file."
              value={form.favicon}
              assets={state.assets}
              disabled={!editable}
              onChange={(next) => set("favicon", next)}
              onUploaded={() => void refetch()}
            />
          </CardBody>
        </Card>

        <div className="border-border bg-surface rounded-container sticky bottom-4 flex flex-wrap items-center gap-3 border p-4 shadow-popover">
          <Button
            variant="primary"
            type="button"
            loading={save.isPending}
            disabled={!dirty || !editable}
            onClick={() => {
              replaceNext.current = true;
              save.mutate(payload, {
                onError: () => (replaceNext.current = false),
              });
            }}
          >
            Apply branding
          </Button>
          {dirty ? (
            <>
              <span className="type-support text-ink-secondary">
                Unsaved changes
              </span>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setForm(base)}
              >
                Discard
              </Button>
            </>
          ) : save.isSuccess ? (
            <span className="type-support text-success">
              Applied. It is live everywhere, including the next emailed PDF.
            </span>
          ) : null}
          {error ? (
            <span className="type-support text-error">{error}</span>
          ) : null}

          <div className="ms-auto flex items-center gap-2">
            <span className="type-support text-ink-disabled">
              {state.overridden.length > 0
                ? `${state.overridden.length} field${state.overridden.length === 1 ? "" : "s"} changed here`
                : "Everything from tokens.json"}
            </span>
            <Button
              type="button"
              variant="ghost"
              disabled={state.overridden.length === 0 || !editable}
              onClick={() => setConfirmReset(true)}
            >
              Reset to file
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Reset branding to the file?"
        description={
          <>
            Every change made here goes back to what{" "}
            <span className="font-mono">{state.tokens_path}</span> says.
          </>
        }
        consequence={`${state.overridden.length} field${state.overridden.length === 1 ? "" : "s"}: ${state.overridden.join(", ")}. Uploaded files stay on disk.`}
        confirmLabel="Reset"
        loading={reset.isPending}
        onConfirm={() => {
          replaceNext.current = true;
          reset.mutate(undefined, {
            onSuccess: () => setConfirmReset(false),
            onError: () => (replaceNext.current = false),
          });
        }}
      />
    </div>
  );
}
